import { Circle, CircleCheck, CircleX } from "lucide-react";
import { formatRelative } from "@/lib/utils";
import { useDealStatusTimeline } from "./use-deal-status-timeline";
import type { DealStatus, DealTimelineEvent } from "@colab/shared";

// Read-only vertical timeline derived from deals.status_history (JSONB,
// populated by trigger tg_deals_status_history). Sub-bucket 5 mirror of
// mdf-decision-timeline. Hoist-deferred per plan quality bar (two
// consumers is not enough to justify generic shared component).
//
// Attribution-only rows return 404 from the timeline endpoint — caller
// gates with `enabled` so this component never mounts on those rows.
//
// Known regression: partner-submitted rows record changed_by='system' (see
// services/deals.ts comment). The display does not surface actor names —
// the audit role distinction is captured in the audit_log proper.

type Props = {
  dealId: string;
  isWithdrawn?: boolean;
};

type TimelineEvent = {
  label: string;
  timestamp: string;
  tone: "neutral" | "success" | "failure";
};

export function DealDecisionTimeline({ dealId, isWithdrawn }: Props) {
  const { data, isLoading, isError } = useDealStatusTimeline(dealId);

  if (isLoading) {
    return (
      <div>
        <h3 className="text-sm font-medium text-foreground">Activity</h3>
        <p className="mt-2 text-xs text-muted-foreground">Loading timeline…</p>
      </div>
    );
  }

  if (isError) {
    // 404 is expected for attribution-only rows; the parent should not have
    // mounted us. Other errors render quietly.
    return null;
  }

  const events = buildEvents(data ?? [], isWithdrawn ?? false);
  if (events.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-medium text-foreground">Activity</h3>
      <ol className="mt-3 space-y-3">
        {events.map((e, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <span
              aria-hidden
              className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center"
            >
              {e.tone === "success" ? (
                <CircleCheck className="h-4 w-4 text-foreground" />
              ) : e.tone === "failure" ? (
                <CircleX className="h-4 w-4 text-destructive" />
              ) : (
                <Circle className="h-2.5 w-2.5 fill-muted-foreground text-muted-foreground" />
              )}
            </span>
            <div className="min-w-0">
              <p className="text-sm text-foreground">{e.label}</p>
              <p className="text-xs text-muted-foreground">
                {formatRelative(e.timestamp)}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function buildEvents(
  history: DealTimelineEvent[],
  isWithdrawn: boolean,
): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  for (const entry of history) {
    // Initial INSERT entry has no `from_status`; render as "Submitted".
    if (!entry.from_status) {
      events.push({
        label: "Submitted",
        timestamp: entry.changed_at,
        tone: "neutral",
      });
      continue;
    }
    events.push(eventForTransition(entry.status, entry.changed_at, isWithdrawn));
  }
  // Sort ascending by timestamp.
  events.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  return events;
}

function eventForTransition(
  status: DealStatus,
  at: string,
  isWithdrawn: boolean,
): TimelineEvent {
  // Withdrawal soft-state: status is closed_lost but the partner thinks of
  // it as "Withdrawn." Distinguish via the soft-state flag.
  if (status === "closed_lost" && isWithdrawn) {
    return { label: "Withdrawn", timestamp: at, tone: "neutral" };
  }
  switch (status) {
    case "under_review":
      return { label: "Under review", timestamp: at, tone: "neutral" };
    case "approved":
      return { label: "Approved", timestamp: at, tone: "success" };
    case "active":
      return { label: "Active", timestamp: at, tone: "success" };
    case "closed_won":
      return { label: "Closed - won", timestamp: at, tone: "success" };
    case "closed_lost":
      return { label: "Closed - lost", timestamp: at, tone: "failure" };
    case "submitted":
      return { label: "Re-opened", timestamp: at, tone: "neutral" };
    default:
      return { label: status, timestamp: at, tone: "neutral" };
  }
}
